import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WorkCard from "@/components/feature/WorkCard";
import { Toast, copyToClipboard, useToast } from "@/components/base/Toast";
import { works } from "@/mocks/works";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = works.find((item) => item.id === id);
  const { isFavorite, toggle } = useFavorites();
  const { message, show } = useToast();

  usePageMeta(
    work ? `《${work.title}》详情 - 大桥未久影视介绍` : "作品详情 - 大桥未久影视网站",
    work
      ? `《${work.title}》是大桥未久主演的${work.type === "movie" ? "电影" : "电视剧"}作品，${work.year} 年上映，类型为${work.genre}。查看剧情简介、演职员信息与相关作品推荐。`
      : "查看大桥未久影视作品的详细资料，包括剧情简介、演职员信息与相关推荐。",
    work
      ? `${work.title},大桥未久,${work.genre},影视作品详情,演员资料`
      : "大桥未久,影视作品详情,演员资料",
  );

  const related = useMemo(() => {
    if (!work) return [];
    return works
      .filter((item) => item.id !== work.id && item.type === work.type)
      .sort((a, b) => Math.abs(a.year - work.year) - Math.abs(b.year - work.year))
      .slice(0, 4);
  }, [work]);

  const handleFavorite = () => {
    if (!work) return;
    const active = isFavorite(work.id);
    toggle(work.id);
    show(active ? "已取消收藏" : "已加入收藏，保存在本机浏览器");
  };

  const handleShare = async () => {
    const success = await copyToClipboard(window.location.href);
    show(success ? "页面链接已复制，可分享给好友" : "复制失败，请手动复制地址栏链接");
  };

  if (!work) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-film-line text-3xl"></i>
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-950">作品不存在或已下线</h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的作品条目可能已调整，请返回作品列表重新选择。
          </p>
          <Link
            to="/category/movie"
            className="mt-6 inline-flex min-h-[44px] cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回作品列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const favorite = isFavorite(work.id);
  const typeLabel = work.type === "movie" ? "电影" : "电视剧";

  return (
    <SiteLayout>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-5 md:px-6 md:py-6">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              {
                label: work.type === "movie" ? "电影作品" : "电视剧作品",
                to: `/category/${work.type}`,
              },
              { label: work.title },
            ]}
          />
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="min-w-0">
            <div className="flex flex-col gap-6 sm:flex-row">
              <div className="w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:w-[240px]">
                <img
                  src={work.cover}
                  alt={`${work.title} 作品封面`}
                  title={`${work.title} 高清封面`}
                  className="h-[320px] w-full object-cover object-top sm:h-[320px]"
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-primary-500 px-2 py-1 text-[11px] font-medium text-background-50">
                    {typeLabel}
                  </span>
                  {work.status === "new" && (
                    <span className="rounded bg-accent-500 px-2 py-1 text-[11px] font-semibold text-primary-950">
                      新作
                    </span>
                  )}
                  <span className="rounded border border-background-300 px-2 py-1 text-[11px] text-foreground-600">
                    {work.year} 年
                  </span>
                </div>

                <h1 className="mt-3 text-2xl font-bold tracking-tight text-foreground-950 md:text-[34px]">
                  {work.title}
                </h1>
                <p className="mt-2 text-sm text-foreground-600">
                  {work.genre}
                  <span className="mx-2 text-background-400">·</span>
                  {work.runtime}
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {work.rating > 0 ? (
                    <span className="flex items-center gap-1.5 rounded-md bg-accent-100 px-3 py-1.5 text-sm font-semibold text-accent-800">
                      <i className="ri-star-fill text-accent-500"></i>
                      {work.rating.toFixed(1)}
                      <span className="text-[11px] font-normal text-accent-700">/ 10</span>
                    </span>
                  ) : (
                    <span className="rounded-md bg-accent-100 px-3 py-1.5 text-sm font-medium text-accent-800">
                      待播 · 评分未开放
                    </span>
                  )}
                  <span className="text-xs text-foreground-500">
                    由大桥未久领衔主演
                  </span>
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-background-200 pt-5 text-sm">
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-400">导演</dt>
                    <dd className="mt-0.5 text-foreground-900">{work.director}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-400">地区 / 语言</dt>
                    <dd className="mt-0.5 text-foreground-900">
                      {work.region} / {work.language}
                    </dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-400">
                      {work.type === "movie" ? "上映年份" : "首播年份"}
                    </dt>
                    <dd className="font-label mt-0.5 text-foreground-900">{work.year}</dd>
                  </div>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-400">片长 / 集数</dt>
                    <dd className="mt-0.5 text-foreground-900">{work.runtime}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2">
                  {work.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-[11px] text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`inline-flex min-h-[48px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 text-sm font-medium transition-colors ${
                      favorite
                        ? "border border-accent-300 bg-accent-100 text-accent-800"
                        : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    <i className={favorite ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                    {favorite ? "已收藏" : "收藏作品"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex min-h-[48px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 text-sm text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
                  >
                    <i className="ri-share-line"></i>
                    分享页面
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-foreground-400">
                  收藏仅保存在本机浏览器，分享为复制当前页面链接。
                </p>
              </div>
            </div>

            <section className="mt-10 border-t border-background-200 pt-8">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                剧情简介
              </h2>
              <div className="mt-4 space-y-3.5 text-sm leading-[1.9] text-foreground-700">
                {work.synopsis.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <p className="mt-4 flex items-start gap-2 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-600">
                <i className="ri-award-line mt-0.5 text-accent-600"></i>
                {work.highlight}
              </p>
            </section>

            <section className="mt-10 border-t border-background-200 pt-8">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                演职员
              </h2>
              <p className="mt-2 text-xs text-foreground-500">
                以下为主要演职人员节选，非完整名单。
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-background-50">
                      大桥
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-950">
                        大桥未久
                      </p>
                      <p className="mt-0.5 text-xs text-foreground-600">
                        {work.cast[0]?.role ?? "主演"}
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                    {work.cast[0]?.note}
                  </p>
                </div>

                {work.cast.slice(1).map((member) => (
                  <div
                    key={member.name}
                    className="rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-sm font-semibold text-secondary-800">
                        {member.name.slice(0, 1)}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-950">
                          {member.name}
                        </p>
                        <p className="mt-0.5 text-xs text-foreground-600">{member.role}</p>
                      </div>
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                      {member.note}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
              <h2 className="text-sm font-semibold text-foreground-950">条目信息</h2>
              <ul className="mt-4 space-y-3 text-xs">
                <li className="flex items-center justify-between gap-3">
                  <span className="text-foreground-500">作品编号</span>
                  <span className="font-label text-foreground-900">{work.id.toUpperCase()}</span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-foreground-500">内容类型</span>
                  <span className="text-foreground-900">{typeLabel}</span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-foreground-500">资料状态</span>
                  <span className="text-foreground-900">已整理 / 持续更新</span>
                </li>
                <li className="flex items-center justify-between gap-3">
                  <span className="text-foreground-500">收藏状态</span>
                  <span className={favorite ? "text-accent-700" : "text-foreground-900"}>
                    {favorite ? "已收藏" : "未收藏"}
                  </span>
                </li>
              </ul>
              <p className="mt-4 border-t border-background-200 pt-4 text-[11px] leading-relaxed text-foreground-500">
                本站仅提供作品资料展示，不提供播放、下载或购买入口。
              </p>
            </div>

            <div className="mt-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground-950">相关作品推荐</h2>
                <Link
                  to={`/category/${work.type}`}
                  className="cursor-pointer text-xs text-foreground-500 transition-colors hover:text-accent-700"
                >
                  查看全部
                </Link>
              </div>
              <div className="space-y-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex cursor-pointer items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 transition-colors hover:border-accent-300"
                  >
                    <div className="h-[72px] w-[54px] shrink-0 overflow-hidden rounded bg-background-200">
                      <img
                        src={item.cover}
                        alt={`${item.title} 封面`}
                        title={`${item.title} 相关推荐`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-sm font-medium text-foreground-900 transition-colors group-hover:text-accent-700">
                        {item.title}
                      </p>
                      <p className="mt-1 text-[11px] text-foreground-500">
                        {item.year} · {item.genre.split(" / ")[0]}
                      </p>
                      <p className="mt-1 flex items-center gap-1 text-[11px] text-foreground-400">
                        <i className="ri-star-fill text-[10px] text-accent-500"></i>
                        {item.rating > 0 ? item.rating.toFixed(1) : "待播"}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12 border-t border-background-200 pt-8">
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-foreground-950">更多{typeLabel}作品</h2>
              <p className="mt-1 text-xs text-foreground-500">
                同为{typeLabel}类型，按年份接近程度推荐
              </p>
            </div>
            <Link
              to={`/category/${work.type}`}
              className="inline-flex min-h-[40px] shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3.5 text-xs text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
            >
              全部{typeLabel}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
            {related.map((item, index) => (
              <WorkCard key={item.id} work={item} delay={index * 50} />
            ))}
          </div>
        </section>
      </div>

      <Toast message={message} />
    </SiteLayout>
  );
}